import ResultPopup from "@components/General/ResultPopup";
import { Loader } from "@shared/designSystem/Loader";
import { usePopup } from "@contexts/popupContext";
import { useTimezone } from "@contexts/useTimezone";
import { Application } from "@core/Application";
import ClickableWrapper from "@shared/designSystem/ClickableWrapper";
import { ColumnsContainer, HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import { HollowedButton } from "@shared/designSystem/Button";
import DetailField from "@shared/designSystem/DetailField";
import { Modal } from "@shared/designSystem/messages/Modal";
import SearchBar from "@shared/designSystem/SearchBar";
import UserCard from "@shared/designSystem/UserCard";
import { Description } from "@shared/utils/DescriptionsHelper";
import { BoldText, SmallText, Text } from "@shared/designSystem/Texts";
import { QSwitch } from "@shared/designSystem/QSwitch";
import styles from "@styles/components/Notificaciones/NotificacionesList.module.css";
import { useState } from "react";

import {
    NotificacionListItem,
    processNotificationName,
    useNotificacionesListScreenViewModel,
} from "./NotificacionesListScreenViewModel";
import designSystemStyles from "@shared/styles/designSystemStyles";

interface NotificacionRowProps {
    row: NotificacionListItem;
    leido: boolean;
    onClick: () => void;
    timezone: string;
}

const columnWidths = {
    user: "36%",
    category: "24%",
    buildingName: "20%",
    time: "20%"
}

const NotificacionRow: React.FC<NotificacionRowProps> = ({ row, leido, onClick, timezone }) => {
    const buildingName = row.building === "Presenteeism Lobby" ? "" : row.building;

    return (
        <ClickableWrapper
            onClick={onClick}
            className={styles["notification-row-clickable"]}
            title={row.description}
            style={{ width: "100%" }}
        >
            <HorizontalContainer
                width="100%"
                backgroundColor={leido ? "" : designSystemStyles.colorBackgroundGray2}
                borderRadius={designSystemStyles.cornerRadiusComponent}
            >
                <HorizontalContainer width={columnWidths.user}>
                    <UserCard
                        name={processNotificationName(row.name)}
                        width="100%"
                    />
                </HorizontalContainer>
                <HorizontalContainer width={columnWidths.category}>
                    <SmallText text={row.categoria} />
                </HorizontalContainer>
                <HorizontalContainer width={columnWidths.buildingName}>
                    <Text text={buildingName} />
                </HorizontalContainer>
                <HorizontalContainer width={columnWidths.time}>
                    <SmallText text={Description.Timestamp.timeAgoWithDate(row.timestamp, timezone)} alignment="right" />
                </HorizontalContainer>
            </HorizontalContainer>
        </ClickableWrapper>
    );
};

interface NotificacionesListScreenProps {
    orgID: string;
}

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
        <Text text={"**"+title+"**"} color={designSystemStyles.colorText} markdown={true}/>
        <VerticalContainer
            width="100%"
            gap={designSystemStyles.gapMd}
            backgroundColor={designSystemStyles.colorBackgroundGray2}
            borderRadius={designSystemStyles.cornerRadiusComponent}
            padding={designSystemStyles.paddingMd}
        >
            {children}
        </VerticalContainer>
    </VerticalContainer>
);

const formatNotificationDateTime = (timestamp: number | undefined, timezone: string) => {
    if (!timestamp) {
        return "N/D".localized();
    }

    return Description.Timestamp.timeAgoWithDate(timestamp, timezone);
};

const NotificacionesListScreen: React.FC<NotificacionesListScreenProps> = ({ orgID }) => {
    const { showPopup } = usePopup();
    const { timezone } = useTimezone();
    const notificationsService = Application.shared().coreDependencies.notificationsService;
    const [selectedNotification, setSelectedNotification] = useState<NotificacionListItem | null>(null);

    const viewModel = useNotificacionesListScreenViewModel({
        orgID,
        notificationsService,
    });

    const handleMarkAllAsRead = async () => {
        try {
            await viewModel.markAllAsRead();
            showPopup(() => <ResultPopup result="Notificaciones marcadas como leídas" succesful={true} />);
        } catch (error) {
            showPopup(() => <ResultPopup result="Hubo un error" succesful={false} />);
            console.error(error);
        }
    };

    return (
        <VerticalContainer height={"100%"} scrollable={true} margin={designSystemStyles.paddingZero}>

        <HorizontalContainer>
            <SearchBar
                placeholder={"filtrar".localized()}
                value={viewModel.searchField} 
                onChange={viewModel.setSearchField} 
            />
            <HollowedButton
                text={"Marcar todas como leídas".localized()}
                onClick={handleMarkAllAsRead}
                width="240px"
            />
        </HorizontalContainer>

        <div className={styles["actions-container"]}>
            <div className={styles["counter"]}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <path d="M4.64 11.4H1.98732C1.69739 11.4 1.42873 11.2479 1.27957 10.9993V10.9993C1.10992 10.7165 1.12484 10.36 1.3175 10.0924L2.04703 9.07913C2.38055 8.6159 2.56 8.05955 2.56 7.48875V6.85C2.56 5.8639 2.87001 4.90276 3.4462 4.10251L3.64568 3.82544C3.95804 3.39161 4.3623 3.03206 4.82961 2.77244L5.28353 2.52026C5.54327 2.37596 5.76048 2.16587 5.91335 1.91108L5.94427 1.85955C6.11038 1.5827 6.36079 1.36634 6.65882 1.24216V1.24216C7.03077 1.08718 7.44922 1.08718 7.82118 1.24216V1.24216C8.11921 1.36634 8.36962 1.5827 8.53573 1.85955L8.56665 1.91108C8.71952 2.16587 8.93673 2.37596 9.19647 2.52026L9.65039 2.77244C10.1177 3.03206 10.522 3.39161 10.8343 3.82544L11.1313 4.23795C11.6441 4.95014 11.92 5.80552 11.92 6.68311V7.54755C11.92 8.07316 12.1233 8.5784 12.4873 8.95757L13.299 9.80315C13.6779 10.1978 13.6427 10.8311 13.2224 11.1813V11.1813C13.0529 11.3226 12.8391 11.4 12.6184 11.4H9.84M4.64 11.4L4.84723 11.9181C5.05046 12.4261 5.41012 12.8563 5.87414 13.1463V13.1463C6.70982 13.6686 7.77018 13.6686 8.60585 13.1463V13.1463C9.06988 12.8563 9.42954 12.4261 9.63277 11.9181L9.84 11.4M4.64 11.4H9.84" stroke="white" />
                </svg>
                {viewModel.unreadCount}
            </div>

            <div className={styles["actions"]}>
                <div style={{ width: "200px", height: "40px", cursor: "pointer" }} />

                <div className={styles["action"]} style={{ marginLeft: "15px" }}>
                    {"Mostrar sólo las no leidas".localized()}
                    <QSwitch
                        checked={viewModel.mostrarSoloNoLeidos}
                        onChange={viewModel.toggleMostrarSoloNoLeidos}
                        defaultChecked
                    />
                </div>

                <div className={styles["action"]}>
                    # Categoría
                    <div className={styles["svg-container"]} style={{ width: "20px", cursor: "pointer" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 14 14" fill="none">
                            <path d="M3.5 7H10.5M1.75 3.5H12.25M5.25 10.5H8.75" stroke="#344054" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                </div>
            </div>
        </div>

        <div className={styles["table-container"]} style={{
            gap: designSystemStyles.gapSm
        }}>
            {viewModel.filteredRows.map((row) => (
                <NotificacionRow
                    key={`${row.timestamp}-${row.name}-${row.description}`}
                    row={row}
                    leido={viewModel.isRead(row)}
                    timezone={timezone}
                    onClick={() => setSelectedNotification(row)}
                />
            ))}
            {viewModel.loading && (
                <div className={styles["no-qontact-points"]} style={{ marginTop: "25px" }}>
                    <Loader height="48px" width="48px" />
                </div>
            )}
        </div>

        <Modal
            isOpen={selectedNotification != null}
            onClose={() => setSelectedNotification(null)}
            title={selectedNotification?.categoria ?? "Detalle"}
            width="720px"
            height="80vh"
            maxWidth="90vw"
        >
            <VerticalContainer width="100%" height="100%" gap={designSystemStyles.gapLg}>
                {selectedNotification ? (
                    <>
                        <UserCard
                            name={processNotificationName(selectedNotification.name)}
                            width="100%"
                        />

                        <div style={{ width: "100%", flex: 1, minHeight: 0, overflowY: "auto" }}>
                            <VerticalContainer
                                width="100%"
                                gap={designSystemStyles.gapLg}
                                paddingBottom={designSystemStyles.paddingSm}
                            >
                                <DetailSection title={"Resumen".localized()}>
                                    <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                        <DetailField label={"Categoría".localized()} value={selectedNotification.categoria} />
                                        <DetailField
                                            label={"Fecha".localized()}
                                            value={formatNotificationDateTime(selectedNotification.timestamp, timezone)}
                                        />
                                        <DetailField label={"ID de usuario".localized()} value={selectedNotification.userID} />
                                    </ColumnsContainer>
                                </DetailSection>
                                <DetailSection title={"Mensaje".localized()}>
                                    <DetailField label={"Descripción".localized()} value={selectedNotification.description} />
                                </DetailSection>

                                <DetailSection title={"Ubicación".localized()}>
                                    <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                        <DetailField label={"Edificio".localized()} value={selectedNotification.building} />
                                        <DetailField label={"ID de edificio".localized()} value={selectedNotification.buildingID} />
                                    </ColumnsContainer>
                                </DetailSection>


                                <DetailSection title={"Vigencia".localized()}>
                                    <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                        <DetailField
                                            label={"Expira".localized()}
                                            value={selectedNotification.expiryTimestamp != null ? Description.Timestamp.date(selectedNotification.expiryTimestamp, timezone) : null}
                                        />
                                    </ColumnsContainer>
                                </DetailSection>
                            </VerticalContainer>
                        </div>
                    </>
                ) : null}

                <HorizontalContainer justifyContent="flex-end" width="100%">
                    <HollowedButton
                        text={"Cerrar".localized()}
                        onClick={() => setSelectedNotification(null)}
                    />
                </HorizontalContainer>
            </VerticalContainer>
        </Modal>
        </VerticalContainer>
    );
};

export default NotificacionesListScreen;
